import { useState, useEffect } from 'react';
import { Workflow } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePageTitle } from '../../hooks/usePageTitleHook';
import { NotificationBell } from '../NotificationBell';
import { HelpButton } from '../help/HelpButton';
import { useWorkflowPanel } from '../workflow-panel/useWorkflowPanel';
import { WorkflowSheet } from '../workflow-panel/WorkflowSheet';

interface TopBarProps {
  className?: string;
}

export function TopBar({ className }: TopBarProps) {
  const { title } = usePageTitle();
  const { workflows, isLoading, closeSignal } = useWorkflowPanel();
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (closeSignal > 0) setSheetOpen(false);
  }, [closeSignal]);

  const activeCount = workflows.filter((w) => w.status !== 'dismissed').length;
  const showWorkflowButton = !isLoading && activeCount > 0;

  return (
    <header
      className={cn(
        'sticky top-0 z-40 md:hidden',
        'flex items-center justify-between h-[var(--topbar-height)] px-[var(--page-padding-x)]',
        'bg-background border-b border-border',
        className,
      )}
    >
      <h1 className="text-sm font-semibold text-foreground truncate">
        {title || 'Landlord Portal'}
      </h1>
      <div className="flex items-center gap-1 shrink-0">
        {showWorkflowButton && (
          <button
            type="button"
            data-testid="workflow-launcher-pill"
            onClick={() => setSheetOpen(true)}
            className="relative flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-accent/50 active:scale-95 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Workflows — ${activeCount} active`}
            aria-expanded={sheetOpen}
            aria-haspopup="dialog"
          >
            <Workflow className="h-4 w-4" aria-hidden="true" />
            <span
              data-testid="workflow-launcher-badge"
              className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-3.5 h-3.5 px-0.5 text-[10px] font-bold bg-primary text-primary-foreground rounded-full"
              aria-label={`${activeCount} active`}
            >
              {activeCount > 9 ? '9+' : activeCount}
            </span>
          </button>
        )}
        <HelpButton data-testid="help-button-mobile" />
        <NotificationBell />
      </div>
      <WorkflowSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </header>
  );
}
